/**
 * IXD/DSD 비교 화면의 URL 상태(쿼리 파라미터) 관리.
 *
 * - comparisonId: 비교(=리포트) 식별자. 존재하면 대사 화면, 없으면 업로드 화면.
 * - chunkId: 현재 활성 DSD chunk. comparisonId 없이는 의미 없음.
 *
 * 비교/활성 chunk 상태를 URL에 두어, 새로고침해도 대사 화면이 복원되도록 한다.
 * (이전에는 페이지 스코프 store에 보관해 새로고침 시 업로드 화면으로 되돌아갔다.)
 */

import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import {
  PARAM_CHUNK_ID,
  PARAM_COMPARISON_ID,
  buildComparisonUrl,
} from "@/utils/ixd-dsd-compare/comparison-url";

export const useCompareUrlState = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const comparisonId = searchParams.get(PARAM_COMPARISON_ID);
  const activeChunkId = searchParams.get(PARAM_CHUNK_ID);

  /**
   * 비교 결과를 새 탭에서 대사 화면으로 연다 (동기 호출 전용).
   * 업로드 화면(현재 탭)은 그대로 두어 런처처럼 동작한다. 새 탭은 URL의
   * comparisonId/chunkId로 서버에서 summary를 받아 화면을 복원한다
   * (탭 간 React Query 캐시는 공유되지 않는다).
   *
   * 주의: 비동기 작업(업로드/비교) 완료 후 호출하면 팝업 차단에 걸린다.
   * 그 경우엔 클릭 시점에 빈 탭을 미리 연 뒤 buildComparisonUrl로 주소만 주입할 것.
   */
  const openComparisonInNewTab = useCallback(
    (nextComparisonId: string, nextChunkId?: string | null) => {
      window.open(
        buildComparisonUrl(nextComparisonId, nextChunkId),
        "_blank",
        "noopener",
      );
    },
    [],
  );

  /** 활성 chunk 변경(같은 탭). 매 클릭마다 history가 쌓이지 않도록 replace. */
  const setActiveChunkId = useCallback(
    (nextChunkId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (nextChunkId) next.set(PARAM_CHUNK_ID, nextChunkId);
          else next.delete(PARAM_CHUNK_ID);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return {
    comparisonId,
    activeChunkId,
    openComparisonInNewTab,
    setActiveChunkId,
  };
};
